import { create } from 'zustand';
import { scenarios } from '../data/scenarios';
import type { FunnelFilter, Language } from '../types';

export type PlaybackStatus = 'idle' | 'playing' | 'paused' | 'complete';

interface DiagramState {
  language: Language;
  scenarioId: string;
  stepIndex: number;
  elapsedMs: number;
  status: PlaybackStatus;
  speed: number;
  selectedNodeId: string | null;
  hoveredNodeId: string | null;
  roleLens: boolean;
  funnelFilter: FunnelFilter;
  focusPath: boolean;
  sourcesOpen: boolean;
  searchOpen: boolean;
  wasPlayingBeforeHidden: boolean;
  setLanguage: (language: Language) => void;
  setScenario: (id: string) => void;
  play: () => void;
  pause: () => void;
  restart: () => void;
  next: () => void;
  previous: () => void;
  advanceBy: (deltaMs: number) => void;
  setSpeed: (speed: number) => void;
  selectNode: (id: string | null) => void;
  hoverNode: (id: string | null) => void;
  setRoleLens: (enabled: boolean) => void;
  setFunnelFilter: (filter: FunnelFilter) => void;
  setFocusPath: (enabled: boolean) => void;
  setSourcesOpen: (open: boolean) => void;
  setSearchOpen: (open: boolean) => void;
  pauseForVisibility: () => void;
  resumeFromVisibility: () => void;
}

const getScenario = (id: string) => scenarios.find((scenario) => scenario.id === id) ?? scenarios[0];

export const useDiagramStore = create<DiagramState>((set, get) => ({
  language: (localStorage.getItem('sinteks-language') as Language | null) ?? 'en',
  scenarioId: scenarios[0].id,
  stepIndex: 0,
  elapsedMs: 0,
  status: 'idle',
  speed: 1,
  selectedNodeId: null,
  hoveredNodeId: null,
  roleLens: false,
  funnelFilter: 'all',
  focusPath: false,
  sourcesOpen: false,
  searchOpen: false,
  wasPlayingBeforeHidden: false,
  setLanguage: (language) => {
    localStorage.setItem('sinteks-language', language);
    document.documentElement.lang = language;
    set({ language });
  },
  setScenario: (scenarioId) => set({ scenarioId, stepIndex: 0, elapsedMs: 0, status: 'idle' }),
  play: () => {
    const state = get();
    if (state.status === 'complete') set({ stepIndex: 0, elapsedMs: 0, status: 'playing' });
    else set({ status: 'playing' });
  },
  pause: () => set((state) => state.status === 'playing' ? { status: 'paused' } : {}),
  restart: () => set({ stepIndex: 0, elapsedMs: 0, status: 'idle' }),
  next: () => set((state) => {
    const scenario = getScenario(state.scenarioId);
    if (state.stepIndex >= scenario.steps.length - 1) return { status: 'complete', elapsedMs: scenario.steps.at(-1)?.durationMs ?? 0 };
    return { stepIndex: state.stepIndex + 1, elapsedMs: 0, status: state.status === 'playing' ? 'playing' : 'paused' };
  }),
  previous: () => set((state) => ({ stepIndex: Math.max(0, state.stepIndex - 1), elapsedMs: 0, status: 'paused' })),
  advanceBy: (deltaMs) => set((state) => {
    if (state.status !== 'playing') return {};
    const scenario = getScenario(state.scenarioId);
    const current = scenario.steps[state.stepIndex];
    const elapsedMs = state.elapsedMs + deltaMs * state.speed;
    if (elapsedMs < current.durationMs) return { elapsedMs };
    if (state.stepIndex >= scenario.steps.length - 1) return { elapsedMs: current.durationMs, status: 'complete' };
    return { stepIndex: state.stepIndex + 1, elapsedMs: 0 };
  }),
  setSpeed: (speed) => set({ speed }),
  selectNode: (selectedNodeId) => set({ selectedNodeId, focusPath: false }),
  hoverNode: (hoveredNodeId) => set({ hoveredNodeId }),
  setRoleLens: (roleLens) => set({ roleLens }),
  setFunnelFilter: (funnelFilter) => set({ funnelFilter, selectedNodeId: null, hoveredNodeId: null, focusPath: false }),
  setFocusPath: (focusPath) => set({ focusPath }),
  setSourcesOpen: (sourcesOpen) => set({ sourcesOpen }),
  setSearchOpen: (searchOpen) => set({ searchOpen }),
  pauseForVisibility: () => set((state) => ({ wasPlayingBeforeHidden: state.status === 'playing', status: state.status === 'playing' ? 'paused' : state.status })),
  resumeFromVisibility: () => set((state) => ({ status: state.wasPlayingBeforeHidden ? 'playing' : state.status, wasPlayingBeforeHidden: false })),
}));
